import React, { Component } from 'react'
import { observable, reaction, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import styled, { withTheme } from 'styled-components'
import { Alert, FormControl, Form } from 'react-bootstrap'
import { withTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import ArraySelect from '../Select/ArraySelect'
import PasswordInputField from '../Forms/PasswordInputField'

import { ButtonWithLoadingState } from '../Buttons'
import {
  Card,
  CardHead,
  CardHeadIcon,
  CardHeadText,
  CardTitle,
  CardSubtitle,
  CardBody,
  CardFoot,
} from '../../pages/PersonalSpacePage'

const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  margin-bottom: 10px;
  margin-top: 20px;

  &:first-child {
    margin-top: 0;
  }
`

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`

const FieldGroup = styled.div`
  margin-bottom: 14px;

  &:last-of-type {
    margin-bottom: 0;
  }

  /* Override Bootstrap input styling to match modern design */
  .form-control {
    height: 40px;
    font-size: 13.5px;
    border: 1.5px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    color: #111827;
    padding: 0 40px 0 12px;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::placeholder {
      color: #d1d5db;
    }

    &:hover {
      border-color: #d1d5db;
    }

    &:focus {
      border-color: #1a56db;
      box-shadow: 0 0 0 3px rgba(26, 86, 219, 0.09);
      background: #ffffff;
    }

    &.is-invalid {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.09);
    }
  }

  .input-group-text {
    border: 1.5px solid #e5e7eb;
    border-left: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    transition: color 0.15s;

    &:hover {
      color: #374151;
    }
  }

  select.form-control {
    cursor: pointer;
  }

  .invalid-feedback {
    font-size: 12px;
    color: #ef4444;
    margin-top: 5px;
  }
`

const RulesAlert = styled(Alert)`
  && {
    font-size: 12.5px;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
    border: none;
    background: #ebf2ff;
    color: #1447c0;
  }
`

const ErrorAlert = styled(Alert)`
  && {
    font-size: 13px;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 16px;
    border: none;
    background: #fef2f2;
    color: #b91c1c;
  }
`

const SuccessAlert = styled(Alert)`
  && {
    font-size: 13px;
    border-radius: 8px;
    padding: 10px 14px;
    margin-top: 16px;
    border: none;
    background: #f0fdf4;
    color: #15803d;
  }
`

const SaveButtonWrap = styled.div`
  .btn-primary {
    background: #1a56db;
    border: none;
    border-radius: 6px;
    height: 38px;
    padding: 0 22px;
    font-size: 13.5px;
    font-weight: 500;
    letter-spacing: 0.01em;
    transition: background 0.15s, transform 0.1s;
    margin-left:10px;
    &:hover {
      background: #1447c0;
    }

    &:active {
      transform: scale(0.98);
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba(26, 86, 219, 0.2);
    }
  }
`

const CancelLink = styled(Link)`
  && {
    display: inline-flex;
    align-items: center;
    height: 38px;
    padding: 0 18px;
    font-size: 13.5px;
    font-weight: 400;
    color: #6b7280;
    background: #ffffff;
    border: 1.5px solid #e5e7eb;
    border-radius: 6px;
    text-decoration: none;
    transition: all 0.15s;

    &:hover {
      border-color: #d1d5db;
      color: #374151;
      background: #f9fafb;
    }
  }
`

const RequiredNote = styled.span`
  font-size: 11.5px;
  color: #9ca3af;
`

const ScrollAnchor = styled.div`
  position: relative;
  top: -80px;
`
const ActionButtonsWrapper = styled.div`
  display: flex;
`;
class NewPasswordBox extends Component {
  securityQuestions = []
  passwordRules = {}
  scrollTopRef = null
  scrollTopReaction = null

  constructor(props) {
    super(props)

    makeObservable(this, {
      securityQuestions: observable,
      passwordRules: observable,
    })

    this.props.profileStore.passwordNew.isSucceeded = false
    this.scrollTopRef = React.createRef()

    apiClient.getJson(APIEndpoints.passwordRules).then((response) => {
      this.passwordRules = response
    })
  }

  componentDidMount() {
    const { passwordNew } = this.props.profileStore

    reaction(
      () => this.props.profileStore.profile.id,
      (profileID, selfReaction) => {
        const { profile } = this.props.profileStore
        if (profileID) {
          apiClient.getJson(APIEndpoints.securityQuestions).then((response) => {
            this.securityQuestions = response
            if (
              !!response.find((item) => item.id === profile.securityQuestionId)
            ) {
              passwordNew.securityQuestionId = profile.securityQuestionId
            } else if (response && response.length) {
              passwordNew.securityQuestionId = response[0].id
            }
          })
          selfReaction.dispose()
        }
      },
      { fireImmediately: true }
    )

    this.scrollTopReaction = reaction(
      () => this.props.profileStore.passwordNew.submitError,
      (submitError) => {
        if (submitError) {
          this.scrollTopRef.current.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          })
        }
      }
    )
  }

  componentWillUnmount() {
    this.scrollTopReaction()
    this.props.profileStore.resetNewPasswordBox()
  }

  render() {
    const { t, profileStore, withCancelButton, loginStore } = this.props
    profileStore.profile.forcePasswordChange &&
      loginStore.loginData.password &&
      profileStore.setOldPassword(loginStore.loginData.password)

    const securityQuestionRequired =
      !profileStore.profile.hasAnsweredSecurityQuestion

    return (
      <Card delay='0.08s'>
        <ScrollAnchor ref={this.scrollTopRef} />

        <CardHead>
          <CardHeadIcon>
            <svg viewBox='0 0 24 24'>
              <rect x='5' y='11' width='14' height='10' rx='2' />
              <path d='M8 11V7a4 4 0 018 0v4' />
            </svg>
          </CardHeadIcon>
          <CardHeadText>
            <CardTitle>{t('personalSpace:changePassword')}</CardTitle>
            <CardSubtitle>
              {t('personalSpace:changePasswordSubtitle')}
            </CardSubtitle>
          </CardHeadText>
        </CardHead>

        <CardBody>
          {profileStore.passwordNew.submitError && (
            <ErrorAlert variant='danger'>
              {t('loginBox:checkLoginData')}
            </ErrorAlert>
          )}

          <Form id='newPasswordForm' onSubmit={(e) => profileStore.handleOnSetPasswordSubmit(e)}>
            <Form.Group>
              {!(
                profileStore.profile.forcePasswordChange &&
                loginStore.loginData.password
              ) && (
                <>
                  <SectionLabel>{t('passwordReset:oldPassword')}</SectionLabel>
                  <FieldGroup>
                    <PasswordInputField
                      inputKey='formOldPasswordBox'
                      placeholder='••••••••'
                      id='formOldPasswordBox'
                      isInvalid={profileStore.passwordNew.formErrors.oldPwd}
                      onChange={(e) =>
                        profileStore.handleOnChangeOldPassword(e)
                      }
                      autoComplete='none'
                    >
                      <FormControl.Feedback type='invalid'>
                        {profileStore.passwordNew.formErrors.oldPwd}
                      </FormControl.Feedback>
                    </PasswordInputField>
                  </FieldGroup>
                </>
              )}

              <SectionLabel>{t('passwordReset:newPassword')}</SectionLabel>
              {this.passwordRules.hasRules && (
                <RulesAlert variant='info'>
                  {this.passwordRules.info}
                </RulesAlert>
              )}

              <FieldRow>
                <FieldGroup>
                  <PasswordInputField
                    inputKey='formNewPasswordBox'
                    placeholder='••••••••'
                    id='formNewPasswordBox'
                    onChange={(e) => profileStore.handleChangeNewPassword(e)}
                    autoComplete='none'
                    isInvalid={profileStore.passwordNew.formErrors.newPwd}
                  />
                </FieldGroup>

                <FieldGroup>
                  <PasswordInputField
                    inputKey='formNewPasswordBoxRepeat'
                    placeholder={t('passwordReset:newPasswordRepeat')}
                    id='formNewPasswordBoxRepeat'
                    isInvalid={profileStore.passwordNew.formErrors.newPwd}
                    onChange={(e) =>
                      profileStore.handleChangeNewPasswordRepeat(e)
                    }
                    autoComplete='none'
                  >
                    <FormControl.Feedback type='invalid'>
                      {profileStore.passwordNew.formErrors.newPwd}
                    </FormControl.Feedback>
                  </PasswordInputField>
                </FieldGroup>
              </FieldRow>

              <SectionLabel>
                {t('passwordReset:securityQuestion')}
                {securityQuestionRequired ? ' *' : null}
              </SectionLabel>

              <FieldGroup>
                <ArraySelect
                  selectKey='securityQuestions'
                  id='securityQuestions'
                  returnEvent
                  onChange={(e) => profileStore.handleChangeSequrityQuestion(e)}
                  value={profileStore.passwordNew.securityQuestionId}
                  autoComplete='none'
                  options={this.securityQuestions}
                  isInvalid={
                    profileStore.passwordNew.formErrors.securityQuestionId
                  }
                />
                <FormControl.Feedback type='invalid'>
                  {profileStore.passwordNew.formErrors.securityQuestionId}
                </FormControl.Feedback>
              </FieldGroup>

              <SectionLabel>
                {t('passwordReset:answer')}
                {securityQuestionRequired ? ' *' : null}
              </SectionLabel>

              <FieldGroup>
                <PasswordInputField
                  key='securityQuestionsAnswer'
                  type='text'
                  placeholder='•••••••••••'
                  id='securityQuestionsAnswer'
                  required={securityQuestionRequired}
                  isInvalid={
                    profileStore.passwordNew.formErrors.securityQuestionAnswer
                  }
                  onChange={(e) =>
                    profileStore.handleChangeSecurityQuestionAnswer(e)
                  }
                  autoComplete='none'
                >
                  <FormControl.Feedback type='invalid'>
                    {profileStore.passwordNew.formErrors.securityQuestionAnswer}
                  </FormControl.Feedback>
                </PasswordInputField>
              </FieldGroup>

              {profileStore.passwordNew.isSucceeded && (
                <SuccessAlert variant='success'>
                  {t('personalSpace:changedPasswordSuccessfully')}
                </SuccessAlert>
              )}
            </Form.Group>
          </Form>
        </CardBody>

        <CardFoot>
          <RequiredNote>* {t('mandatoryField', 'Pflichtfelder')}</RequiredNote>
          <ActionButtonsWrapper>
            {withCancelButton && (
              <CancelLink to='/'>{t('cancelButton')}</CancelLink>
            )}
            <SaveButtonWrap>
              <ButtonWithLoadingState
                buttonText={t('saveButton')}
                isLoading={profileStore.passwordNew.isLoading}
                type='submit'
                variant='primary'
                form='newPasswordForm'
              />
            </SaveButtonWrap>
          </ActionButtonsWrapper>
        </CardFoot>
      </Card>
    )
  }
}

export default withTheme(
  withTranslation()(
    inject('profileStore', 'loginStore')(observer(NewPasswordBox))
  )
)
